import { create } from 'zustand';
import type { RouteInfo, Restaurant, SearchFilters, TimeWindow } from '@bilmat/shared';
import { deriveTimeWindow, EAT_TOLERANCE_MIN } from '../lib/timeWindow';

export type ScreenId = 1 | 2 | 3 | 4;
export type SortMode = 'omvag' | 'betyg';
export type ThemeMode = 'light' | 'dark';
export type SheetState = 'peek' | 'half' | 'full';

export type LocationInput = {
  label: string;
  placeId: string | null;
};

type AppState = {
  screen: ScreenId;
  origin: LocationInput;
  destination: LocationInput;
  route: RouteInfo | null;
  timeWindow: TimeWindow;
  /** Valt målvärde ("äta om X min in i resan"); 0 = så snart som möjligt. */
  eatTargetMin: number;
  restaurants: Restaurant[];
  restaurantsKey: string | null;
  /** Antal genomförda sökningar (kumulativt, persisterat) — styr install-modalen. */
  searchCount: number;
  filters: SearchFilters;
  sort: SortMode;
  theme: ThemeMode;
  selectedPlaceId: string | null;
  sheetState: SheetState;

  setScreen: (screen: ScreenId) => void;
  setOrigin: (value: LocationInput) => void;
  setDestination: (value: LocationInput) => void;
  setRoute: (route: RouteInfo | null) => void;
  setEatTarget: (targetMin: number, totalMin: number) => void;
  setSearchResults: (items: Restaurant[], key: string | null) => void;
  setFilters: (filters: SearchFilters) => void;
  setSort: (sort: SortMode) => void;
  setTheme: (theme: ThemeMode) => void;
  selectRestaurant: (placeId: string | null) => void;
  setSheetState: (s: SheetState) => void;
  reset: () => void;
};

const emptyFilters: SearchFilters = {
  price: [],
  cuisine: [],
  family: [],
  preferences: [],
};

const initialTheme: ThemeMode =
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

const emptyLocation: LocationInput = { label: '', placeId: null };

const SEARCH_COUNT_KEY = 'bilmat-search-count';

function readSearchCount(): number {
  if (typeof window === 'undefined') return 0;
  try {
    return Number(window.localStorage.getItem(SEARCH_COUNT_KEY)) || 0;
  } catch {
    return 0;
  }
}

export const useApp = create<AppState>((set) => ({
  screen: 1,
  origin: emptyLocation,
  destination: emptyLocation,
  route: null,
  timeWindow: { earliestMin: 0, latestMin: EAT_TOLERANCE_MIN },
  eatTargetMin: 0,
  restaurants: [],
  restaurantsKey: null,
  searchCount: readSearchCount(),
  filters: emptyFilters,
  sort: 'omvag',
  theme: initialTheme,
  selectedPlaceId: null,
  sheetState: 'half',

  setScreen: (screen) => set({ screen }),
  setOrigin: (origin) => set({ origin }),
  setDestination: (destination) => set({ destination }),
  setRoute: (route) => set({ route }),
  setEatTarget: (eatTargetMin, totalMin) =>
    set({ eatTargetMin, timeWindow: deriveTimeWindow(eatTargetMin, totalMin) }),
  setSearchResults: (restaurants, restaurantsKey) =>
    set((state) => {
      // Räkna varje genomförd sökning (nytt, icke-tomt sök-id) — kumulativt.
      const isNewSearch = restaurantsKey !== null && restaurantsKey !== state.restaurantsKey;
      const searchCount = isNewSearch ? state.searchCount + 1 : state.searchCount;
      if (isNewSearch && typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(SEARCH_COUNT_KEY, String(searchCount));
        } catch {
          /* localStorage kan vara blockerat — strunta i det */
        }
      }
      return { restaurants, restaurantsKey, searchCount };
    }),
  setFilters: (filters) => set({ filters }),
  setSort: (sort) => set({ sort }),
  setTheme: (theme) => set({ theme }),
  selectRestaurant: (selectedPlaceId) => set({ selectedPlaceId }),
  setSheetState: (sheetState) => set({ sheetState }),
  reset: () =>
    set({
      screen: 1,
      route: null,
      restaurants: [],
      restaurantsKey: null,
      filters: emptyFilters,
      selectedPlaceId: null,
      sort: 'omvag',
      sheetState: 'half',
    }),
}));
